"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, MailCheck, ShieldAlert, SlidersHorizontal as Sliders, Sparkles, ArrowRight, Clock, ExternalLink, RotateCcw } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface PolicyItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  tag: string;
}
const policyItems: PolicyItem[] = [{
  id: "consent",
  title: "Explicit opt-in consent",
  subtitle: "Clear intent before any delivery",
  description: "We never add your email address to editorial newsletters or research releases without proactive, affirmative consent. Pre-checked checkboxes and automated subscription bundling are strictly prohibited across all MinfoMedia assets.",
  tag: "Standard 01"
}, {
  id: "verification",
  title: "Double opt-in verification",
  subtitle: "Two-step identity confirmation",
  description: "Every new newsletter subscription or research dispatch request requires email validation before activation. A confirmation link is delivered to verify mailbox ownership and prevent unsolicited third-party signups.",
  tag: "Standard 02"
}, {
  id: "cadence",
  title: "Strict publication caps",
  subtitle: "Curated frequency limits",
  description: "Subscribers receive no more than two editorial digests per week alongside quarterly market briefs. We avoid promotional blast sequences, respecting your inbox space with scheduled, structured dispatches.",
  tag: "Standard 03"
}, {
  id: "unsubscribe",
  title: "One-click unsubscribe mechanism",
  subtitle: "Immediate and permanent removal",
  description: "Every email includes an automated, direct unsubscribe link in the header and footer. Processing occurs instantly with zero cooling periods, secondary confirmations, or mandatory login hurdles.",
  tag: "Standard 04"
}];
const dispatchCategories = [{
  title: "Executive Research Briefs",
  frequency: "Monthly",
  scope: "Deep-dive analytical papers and peer-reviewed industry benchmark reports."
}, {
  title: "Weekly Industry Digest",
  frequency: "Weekly (Thursdays)",
  scope: "Curated summary of market dynamics, data infographics, and editorial essays."
}, {
  title: "Quarterly Strategic Outlook",
  frequency: "Quarterly",
  scope: "Long-range forecasting, executive roundtable transcripts, and special editions."
}];
export default function Preferences() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<string>("consent");
  const activePolicy = policyItems.find(item => item.id === activeTab) || policyItems[0];
  return <section id="marketing-communications-preferences" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-28 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead rule divider */}
        <div className="mb-10 sm:mb-12">
          <div className="h-[1px] bg-primary w-full" />
          <div className="h-[0.5px] bg-border w-full mt-[3px]" />
        </div>

        {/* Section Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-14 sm:mb-16">
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-sm border border-border bg-card text-[11px] font-mono tracking-normal text-muted-foreground uppercase">
                Privacy Standard § 4.2
              </span>
              <span className="text-xs font-mono text-muted-foreground">Updated Bi-Annually</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight leading-[1.18] text-foreground">
              Marketing communications &amp; subscriber preferences
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3">
            <p className="text-base sm:text-lg leading-relaxed text-muted-foreground font-sans">
              We treat your inbox as a focused reading environment. MinfoMedia enforces verifiable consent,
              rigorous frequency caps, and single-click automated preference controls across all editorial communications.
            </p>
          </div>
        </div>

        {/* Two-Column Split Architecture */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Interactive Policy Tabs & Detail */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {policyItems.map((item, i) => {
              const isActive = activeTab === item.id;
              return <Button variant="ghost" key={item.id} data-index={i} onClick={() => setActiveTab(item.id)} className={`h-auto whitespace-normal font-normal text-left p-4 rounded-md border transition-[color,background-color,border-color,box-shadow] cursor-pointer flex flex-col justify-between items-stretch min-h-[110px] ${isActive ? "bg-card border-primary ring-1 ring-primary shadow-xs" : "bg-muted/40 border-border hover:bg-card hover:border-border/80"}`}>
                    <div className="flex items-center justify-between w-full mb-2">
                      <span className="text-[11px] font-mono tracking-wider uppercase text-muted-foreground">
                        {item.tag}
                      </span>
                      {isActive ? <CheckCircle2 className="size-4 text-primary" /> : <span className="size-2 rounded-full bg-border" />}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground tracking-normal">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.subtitle}
                      </p>
                    </div>
                  </Button>;
            })}
            </div>

            {/* Active Policy Showcase Card */}
            <motion.div key={activePolicy.id} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="border border-border bg-card shadow-sm rounded-md overflow-hidden">
                <CardHeader className="border-b border-border/70 pb-5 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-chart-4" />
                      <span className="text-xs font-mono uppercase text-muted-foreground">Active Compliance Rule</span>
                    </div>
                    <span className="text-xs font-mono font-medium text-foreground bg-secondary px-2 py-0.5 rounded-sm">
                      {activePolicy.tag}
                    </span>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-serif font-semibold mt-3 text-foreground">
                    {activePolicy.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    {activePolicy.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 pb-6 space-y-4">
                  <p className="text-base text-foreground/90 leading-relaxed font-sans">
                    {activePolicy.description}
                  </p>
                  <div className="p-3.5 rounded-sm bg-muted/30 border border-border flex items-start gap-3 text-xs text-muted-foreground">
                    <MailCheck className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>
                      All transmission logs and consent timestamps are cryptographically archived in compliance with global GDPR Article 7 requirements.
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right Column: Editorial Dispatch Schedule & Self-Service Portal */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="bg-card border border-border rounded-md p-6 sm:p-7 shadow-sm">
              <div className="flex items-center justify-between mb-5 border-b border-border pb-4">
                <div className="flex items-center gap-2">
                  <Sliders className="size-4 text-primary" />
                  <h3 className="font-serif text-lg font-semibold text-foreground">
                    Publication Schedule &amp; Caps
                  </h3>
                </div>
                <Clock className="size-4 text-muted-foreground" />
              </div>

              <div className="space-y-4">
                {dispatchCategories.map((dispatch, i) => <div key={dispatch.title} data-index={i} className="p-3.5 rounded-sm bg-muted/30 border border-border/80 flex flex-col gap-1.5 transition-colors hover:border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">
                        {dispatch.title}
                      </span>
                      <span className="text-[11px] font-mono text-muted-foreground px-1.5 py-0.5 rounded-xs bg-secondary">
                        {dispatch.frequency}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {dispatch.scope}
                    </p>
                  </div>)}
              </div>

              <div className="mt-6 pt-5 border-t border-border space-y-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
                  <span>Self-service subscriber console</span>
                  <span>Direct API</span>
                </div>
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-sm text-sm font-medium transition-[background-color,box-shadow]">
                  <Link to="/NewsletterPage">
                    Manage Email Subscriptions
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Unsubscribe Guarantee Callout */}
            <div className="rounded-md border border-border/80 bg-muted/20 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <RotateCcw className="size-3.5 text-chart-4" />
                  <span>Immediate Opt-Out Guarantee</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Lost access to your preference link? Submit an automated unsubscribe request with your registered email.
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="rounded-sm border-border bg-card text-foreground hover:bg-secondary h-9 text-xs font-medium shrink-0">
                <Link to="/TermsConditionsPage">
                  Review Terms
                  <ExternalLink className="size-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Institutional Assurance Footer Note */}
        <div className="mt-12 pt-6 border-t border-border/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-muted-foreground font-sans">
          <div className="flex items-center gap-2">
            <ShieldAlert className="size-4 text-muted-foreground shrink-0" />
            <span>
              MinfoMedia never rents, sells, or exchanges subscriber registries with commercial sponsors or third-party syndicates.
            </span>
          </div>
          <span className="font-mono text-[11px] shrink-0">ISO/IEC 27001 &amp; GDPR Compliant</span>
        </div>
      </div>
    </section>;
}